import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';

const KURS_LABELS = {
  pirat: '🏴‍☠️ Pirat', seepferdchen: '🐴 Seepferdchen', trixi: '🌟 Trixi',
  bronze: '🥉 Bronze', silber: '🥈 Silber', gold: '🥇 Gold',
  hai: '🦈 Hai', la_silber: '🦈 LA Silber', la_gold: '🦈 LA Gold',
};
const TAGE_KURZ = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']; // DB: 0 = Montag

export function terminText(t) {
  return `${TAGE_KURZ[t.wochentag] ?? ''} ${t.uhrzeit_von?.slice(0, 5) || ''}${t.uhrzeit_bis ? `–${t.uhrzeit_bis.slice(0, 5)}` : ''} Uhr`;
}

// Wo, bei wem und in welcher Gruppe ein Kind schwimmt
export function GruppenInfo({ gruppen, kompakt = false }) {
  if (!gruppen || gruppen.length === 0) {
    return <div style={{ fontSize: '0.8rem', color: '#999' }}>Keiner Gruppe zugeordnet</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: kompakt ? '0.3rem' : '0.5rem' }}>
      {gruppen.map(g => (
        <div key={g.gruppe_id} style={{ fontSize: kompakt ? '0.78rem' : '0.85rem', color: '#444', lineHeight: 1.45 }}>
          <div style={{ fontWeight: 700, color: '#1565c0' }}>
            {g.gruppe_name}
            <span style={{ fontWeight: 400, color: '#777' }}> · {KURS_LABELS[g.kurs_typ] || g.kurs_typ}</span>
          </div>
          {g.trainer_name && <div>👤 {g.trainer_name}</div>}
          {g.ort && <div>📍 {g.ort}</div>}
          {(g.termine || []).length > 0 && <div>📅 {g.termine.map(terminText).join(', ')}</div>}
        </div>
      ))}
    </div>
  );
}

// Suchfeld für die blaue Kopfzeile. onSchliessen blendet es wieder aus.
export default function TrainerSuche({ onSchliessen }) {
  const navigate = useNavigate();
  const eingabe = useRef(null);
  const [text, setText] = useState('');
  const [treffer, setTreffer] = useState([]);
  const [laden, setLaden] = useState(false);
  const [fehler, setFehler] = useState('');

  useEffect(() => { eingabe.current?.focus(); }, []);

  // Verzögert suchen, damit nicht bei jedem Tastendruck eine Anfrage rausgeht
  useEffect(() => {
    const suchtext = text.trim();
    if (suchtext.replace(/\s/g, '').length < 2) { setTreffer([]); setLaden(false); setFehler(''); return undefined; }
    let aktuell = true;
    setLaden(true);
    const timer = setTimeout(() => {
      api.get('/api/trainer/schwimmer/suche', { params: { q: suchtext } })
        .then(r => { if (aktuell) { setTreffer(r.data); setFehler(''); } })
        .catch(() => { if (aktuell) setFehler('Suche fehlgeschlagen'); })
        .finally(() => { if (aktuell) setLaden(false); });
    }, 250);
    return () => { aktuell = false; clearTimeout(timer); };
  }, [text]);

  const oeffne = (s) => {
    onSchliessen();
    navigate(`/trainer/schwimmer/${s.id}`);
  };

  const handleTaste = (e) => {
    if (e.key === 'Escape') onSchliessen();
    if (e.key === 'Enter' && treffer.length === 1 && !laden) oeffne(treffer[0]);
  };

  const zuKurz = text.trim().replace(/\s/g, '').length < 2;

  return (
    <>
      <input
        ref={eingabe}
        type="search"
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={handleTaste}
        placeholder="Kind suchen (Vor- oder Nachname)"
        aria-label="Kind suchen"
        autoComplete="off"
        enterKeyHint="search"
        style={{
          flex: 1, minWidth: 0, height: 38, border: 'none', borderRadius: 8,
          padding: '0 0.75rem', fontSize: '16px', // 16px verhindert das Auto-Zoomen auf dem iPhone
          color: '#222', background: '#fff', outline: 'none',
        }}
      />
      <button onClick={onSchliessen} aria-label="Suche schließen" style={{
        background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff',
        borderRadius: 6, width: 38, height: 38, fontSize: '1.1rem', cursor: 'pointer', flexShrink: 0,
      }}>✕</button>

      {!zuKurz && (
        <div style={{
          position: 'absolute', top: 56, left: 0, right: 0,
          maxHeight: 'calc(100dvh - 70px)', overflowY: 'auto',
          display: 'flex', justifyContent: 'center', padding: '0 0.5rem',
        }}>
          <div style={{
            width: '100%', maxWidth: 640, background: '#fff', color: '#222',
            borderRadius: '0 0 12px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', overflow: 'hidden',
          }}>
            {laden && treffer.length === 0 && (
              <div style={{ padding: '1rem', color: '#888', fontSize: '0.88rem' }}>Suche…</div>
            )}
            {fehler && <div style={{ padding: '1rem', color: '#c62828', fontSize: '0.88rem' }}>{fehler}</div>}
            {!laden && !fehler && treffer.length === 0 && (
              <div style={{ padding: '1rem', color: '#888', fontSize: '0.88rem' }}>Kein Kind mit diesem Namen gefunden.</div>
            )}

            {treffer.length > 1 && (
              <div style={{ padding: '0.5rem 1rem', fontSize: '0.75rem', color: '#777', borderBottom: '1px solid #f0f0f0' }}>
                {treffer.length === 25 ? 'Mehr als 25 Treffer – bitte genauer suchen.' : `${treffer.length} Treffer – bitte auswählen`}
              </div>
            )}

            {treffer.map((s, i) => (
              <button key={s.id} type="button" onClick={() => oeffne(s)} style={{
                display: 'flex', gap: '0.75rem', width: '100%', textAlign: 'left',
                padding: '0.75rem 1rem', background: '#fff', border: 'none', cursor: 'pointer',
                borderBottom: i < treffer.length - 1 ? '1px solid #f0f0f0' : 'none', color: '#222',
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%', background: '#e3f2fd', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, color: '#1565c0', fontSize: '0.85rem',
                }}>
                  {s.vorname?.[0]}{s.nachname?.[0]}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>
                    {s.vorname} {s.nachname}
                    {!s.aktiv_im_verein && (
                      <span style={{ marginLeft: 6, fontSize: '0.68rem', fontWeight: 700, color: '#78909c',
                        background: '#eceff1', borderRadius: 10, padding: '1px 7px' }}>ehemalig</span>
                    )}
                  </div>
                  {s.geburtsdatum && (
                    <div style={{ fontSize: '0.75rem', color: '#888', marginBottom: '0.3rem' }}>
                      * {new Date(s.geburtsdatum).toLocaleDateString('de-DE')}
                    </div>
                  )}
                  <GruppenInfo gruppen={s.gruppen} kompakt />
                </div>
                <span style={{ color: '#bbb', fontSize: '1.1rem', alignSelf: 'center' }}>›</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
